import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'transmute/get';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { UnauthedHubSpotUserAvatar } from 'avatar-components/UnauthedHubSpotUserAvatar';
import { getTextColor } from 'MeetingsBase/utils/color';
import { getIsRoundRobin, getHeaderType, getHeaderOptions } from 'MeetingsLib/utils/utils';
import { getBookInfo as getBookInfoSelector, getOffline as getOfflineSelector } from 'MeetingsLib/selectors/BookInfoSelectors';
import { getIsFetchingPublicAvailability, getIsFetchingCompanyName, getMeetingsCancelStatus } from 'MeetingsLib/selectors/RequestStatusSelectors';
import { SUCCEEDED } from 'MeetingsBase/constants/RequestStatusTypes';
import UserProfile from 'MeetingsBase/models/UserProfile';
import { View } from 'foundations-components/components/View';
import { Image } from 'foundations-components/transitional/components/Image';
import { Flex } from 'foundations-components/components/Flex';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Text } from 'foundations-components/components/Text';
import { withTheme } from 'foundations-theming/utils';
import { ContactsIcon } from 'foundations-assets/icons/components/ContactsIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
class ProfileHeader extends Component {
  constructor(...args) {
    super(...args);
    this.getDefaultHeadline = () => {
      const {
        profile,
        companyName,
        linkType,
        offline,
        isFetchingCompanyName
      } = this.props;
      const isRoundRobin = getIsRoundRobin(linkType);
      if (isRoundRobin && isFetchingCompanyName) {
        return null;
      }
      const suffix = offline ? 'offline' : 'online';
      const headerType = getHeaderType(isRoundRobin);
      const options = getHeaderOptions(isRoundRobin, companyName, profile);
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: `prebook.${headerType}.${suffix}`,
        options: options
      });
    };
    this.getCancelHeadline = () => {
      const {
        cancelRequestStatus
      } = this.props;
      if (cancelRequestStatus === SUCCEEDED) {
        return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "cancel.success.header"
        });
      }
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "cancel.confirm.header"
      });
    };
    this.renderImage = () => {
      const {
        avatar,
        booked,
        isFetchingPublicAvailability
      } = this.props;
      if (isFetchingPublicAvailability) {
        return this.renderSkeletonImage();
      }
      if (booked) {
        return this.renderBookedImage();
      }
      if (avatar) {
        return this.renderAvatarImage();
      }
      return this.renderUserImage();
    };
    this.renderBookedImage = () => {
      const {
        offline
      } = this.props;
      const name = offline ? 'successful-send' : 'success';
      return /*#__PURE__*/_jsx(Illustration, {
        "data-test-id": "booked-image",
        name: name,
        width: "40%",
        "aria-hidden": true
      });
    };
    this.renderSkeletonImage = () => {
      return /*#__PURE__*/_jsx(View, {
        "aria-hidden": true,
        "data-test-id": "skeleton-image",
        children: /*#__PURE__*/_jsx(IconHolder, {
          size: "900",
          icon: ContactsIcon
        })
      });
    };
    this.renderAvatarImage = () => {
      return /*#__PURE__*/_jsx(Image, {
        "data-test-id": "avatar-image",
        responsive: true
        // @ts-expect-error fix type
        ,
        src: this.props.avatar,
        shape: "circle",
        "aria-hidden": true,
        blockSize: "72px",
        inlineSize: "72px"
      });
    };
    this.renderUserImage = () => {
      const {
        profile
      } = this.props;
      const email = profile.email;
      if (!email) return this.renderSkeletonImage();
      return (
        /*#__PURE__*/
        // the avatar component doesn't support alt text, and doesn't provide useful additional info
        // so let's make this hidden to screen readers
        _jsx(UnauthedHubSpotUserAvatar, {
          hubSpotUserEmail: email,
          size: "lg",
          showTooltip: false
        })
      );
    };
    this.renderMessage = () => {
      const {
        headline,
        booked,
        cancelling,
        offline,
        isFetchingPublicAvailability,
        isRescheduling
      } = this.props;
      if (isFetchingPublicAvailability) {
        return null;
      }
      if (cancelling) {
        return this.getCancelHeadline();
      }
      if (booked) {
        const suffix = offline ? 'offline' : 'online';
        const rescheduleSuffix = offline ? 'offlineReschedule' : 'onlineReschedule';
        const desiredSuffix = isRescheduling ? rescheduleSuffix : suffix;
        return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: `summary.header.${desiredSuffix}`
        });
      }
      if (headline) {
        return headline;
      }
      return this.getDefaultHeadline();
    };
    this.renderHeadline = () => {
      const {
        branding: {
          primaryColor
        },
        theme,
        booked
      } = this.props;
      const textColorToken = booked ? 'text-core-default' : getTextColor(primaryColor, theme, true);
      return /*#__PURE__*/_jsx(Text, {
        as: "h2",
        "aria-level": 1,
        color: textColorToken,
        textAlign: "center",
        marginBlockStart: "400",
        marginBlockEnd: "0",
        marginInline: "100",
        children: this.renderMessage()
      });
    };
  }
  render() {
    return /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      alignItems: "center",
      alignSelf: "center",
      "data-test-id": "profile-header",
      children: [this.renderImage(), this.renderHeadline()]
    });
  }
}
ProfileHeader.propTypes = {
  // passed
  avatar: PropTypes.string,
  booked: PropTypes.bool.isRequired,
  cancelling: PropTypes.bool,
  headline: PropTypes.string,
  profile: PropTypes.instanceOf(UserProfile).isRequired,
  // loaded
  companyName: PropTypes.string,
  isFetchingCompanyName: PropTypes.bool,
  isFetchingPublicAvailability: PropTypes.bool,
  cancelRequestStatus: PropTypes.string,
  linkType: PropTypes.string,
  offline: PropTypes.bool,
  isRescheduling: PropTypes.bool,
  // hoc
  theme: PropTypes.object.isRequired
};
const mapStateToProps = (state, ownProps) => {
  return {
    activeLocale: state.activeLocale,
    branding: state.branding,
    cancelRequestStatus: getMeetingsCancelStatus(state),
    companyName: state.companyName,
    isFetchingCompanyName: getIsFetchingCompanyName(state),
    isFetchingPublicAvailability: getIsFetchingPublicAvailability(state),
    isRescheduling: state.isRescheduling,
    /*  @ts-expect-error Jun-18-2025, 19:10UTC fix types  */
    linkType: get('linkType', getBookInfoSelector(state)),
    /*  @ts-expect-error Jun-18-2025, 19:10UTC fix types  */
    offline: getOfflineSelector(state) || ownProps && ownProps.offline
  };
};

/*  @ts-expect-error Jun-18-2025, 19:10UTC fix types  */
export default connect(mapStateToProps)(withTheme(ProfileHeader));